const baseUrl = process.env.API_BASE_URL;

/**
 * Insère un texte dans une cellule Excel spécifiée.
 * @param {string} text - Le texte à insérer.
 * @param {string} caseTexte - L'adresse de la cellule cible (par exemple, "A1").
 */
export async function insertText(text: string, caseTexte: string) {
  try {
    await Excel.run(async (context) => {
      const sheet = context.workbook.worksheets.getActiveWorksheet();
      const range = sheet.getRange(caseTexte);

      range.values = [[text]];
      range.format.autofitColumns();

      await context.sync();
    });
  } catch (error) {
    console.error("Error inserting text into Excel: " + error);
  }
}

/**
 * Génère une réponse à un prompt et insère le résultat dans une cellule Excel.
 * @param {string} prompt - La requête ou question à envoyer à l'IA.
 * @param {string} targetCase - L'adresse de la cellule cible ou "-1" pour utiliser la cellule sélectionnée.
 */
export async function respondToCasePrompt(prompt: string, targetCase: string) {
  try {
    await Excel.run(async (context) => {
      const sheet = context.workbook.worksheets.getActiveWorksheet();
      const targetCell =
        targetCase === "-1"
          ? context.workbook.getSelectedRange().getCell(0, 0)
          : sheet.getRange(targetCase);

      const result = await promptGemini(prompt);
      targetCell.values = [[result]];

      await context.sync();
    });
  } catch (error) {
    console.error("Error responding to case prompt: " + error);
  }
}

/**
 * Standardise ou extrait des données dans une plage de cellules Excel.
 * @param {string[]} cellsToStandardised - Plage de cellules à traiter.
 * @param {number} offset - Décalage pour placer les résultats.
 * @param {string} standardFormat - Format ou requête pour la standardisation.
 * @param {string} calledFunction - Fonction appelée ("extract" ou "standardise").
 * @returns {Promise<number>} - Retourne 1 si réussi, -1 si crédits insuffisants, -2 en cas d'erreur.
 */
export async function manipulateData(
  cellsToStandardised: string[],
  offset: number,
  standardFormat: string,
  calledFunction: string
) {
  try {
    return await Excel.run(async (context) => {
      const worksheet = context.workbook.worksheets.getActiveWorksheet();
      const sourceRange =
        cellsToStandardised && cellsToStandardised.length === 2
          ? worksheet.getRange(`${cellsToStandardised[0]} : ${cellsToStandardised[1]}`)
          : context.workbook.getSelectedRange();

      sourceRange.load("values");
      await context.sync();

      const values = sourceRange.values as any[][];
      const flattenedValues = values.flat();

      const standardisedFlattenedValues = await sendToAPIForStandardisation(
        flattenedValues,
        standardFormat,
        calledFunction
      );

      if (standardisedFlattenedValues[0] == "Crédits insuffisants.") {
        return -1;
      }

      const standardisedValues = reshapeArray(
        standardisedFlattenedValues,
        values.length,
        values[0].length
      );

      const targetRange = sourceRange.getOffsetRange(0, offset);
      targetRange.values = standardisedValues;
      await context.sync();
      return 1;
    });
  } catch (error) {
    console.error(`Error in ${calledFunction} process:`, error);
    return -2;
  }
}

/**
 * Envoie des données à une API pour standardisation ou extraction.
 * @param {any[]} values - Les données à envoyer à l'API.
 * @param {string} standardFormat - Le format ou la requête pour la standardisation ou l'extraction.
 * @param {string} calledFunction - La fonction appelée ("extract" pour extraction ou "standardise" pour standardisation).
 * @returns {Promise<any[]>} - Retourne un tableau contenant les résultats standardisés ou extraits.
 * @throws {Error} - Lance une erreur si l'API répond avec un statut non valide ou si une autre erreur se produit.
 */
async function sendToAPIForStandardisation(
  values: any[],
  standardFormat: string,
  calledFunction: string
): Promise<any[]> {
  const functionAPICall: Record<string, string> = {
    extract: "extractInfo",
    standardise: "standardize",
  };

  try {
    const data = {
      userRequest: standardFormat,
      data: values,
    };

    const response = await fetch(`${baseUrl}/callAI`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify([
        functionAPICall[calledFunction],
        {
          data: data,
        },
      ]),
    });

    if (!response.ok) {
      throw new Error(`API responded with status ${response.status}`);
    }

    const { response: standardisedString } = await response.json();
    if (standardisedString == "Crédits insuffisants.") return ["Crédits insuffisants."];

    return standardisedString.split(";;");
  } catch (error) {
    console.error(`Error in sendToAPIForStandardisation:`, error);
    throw error;
  }
}

/**
 * Réorganise un tableau plat en un tableau 2D avec un nombre spécifié de lignes et de colonnes.
 * @param {any[]} flatArray - Le tableau plat à réorganiser.
 * @param {number} numRows - Le nombre de lignes dans le tableau 2D.
 * @param {number} numCols - Le nombre de colonnes dans le tableau 2D.
 * @returns {any[][]} - Retourne un tableau 2D réorganisé.
 */
function reshapeArray(flatArray: any[], numRows: number, numCols: number): any[][] {
  const result: any[][] = [];
  let index = 0;
  for (let i = 0; i < numRows; i++) {
    const row: any[] = [];
    for (let j = 0; j < numCols; j++) {
      row.push(flatArray[index]);
      index++;
    }
    result.push(row);
  }
  return result;
}

/**
 * Pose une question à une API d'IA et retourne la réponse.
 * @param {string} request - Question ou requête à envoyer.
 * @returns {Promise<string>} - Réponse de l'IA.
 */
export async function askBookQuestion(request: string) {
  var reply = "";
  try {
    const response = await fetch(`${baseUrl}/callAI`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify([
        "bookChat", // calledFunction
        {
          // parameters
          data: {
            userRequest: request,
          },
        },
      ]),
    });
    const data = await response.json();
    reply = data.response;
    return reply;
  } catch (error) {
    console.error("Error:", error);
    return "Je ne peux pas repondre à cette question.";
  }
}

/**
 * Envoie une requête générale à une API et insère les résultats dans Excel.
 * @param {boolean} onlySelected - Indique si seules les cellules sélectionnées doivent être utilisées.
 * @param {string} request - Requête utilisateur.
 * @returns {Promise<number>} - Retourne 1 si réussi, -1 si crédits insuffisants.
 */
export async function askAndExecuteGeneralRequest(onlySelected: boolean, request: string) {
  const excelDictionary = await excelToDict(onlySelected);
  var stringToParse = "";
  try {
    const response = await fetch(`${baseUrl}/callAI`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify([
        "generalAss", // calledFunction
        {
          // parameters
          data: {
            excelDictionary: excelDictionary,
            userRequest: request,
          },
        },
      ]),
    });
    const data = await response.json();
    stringToParse = data.response;
  } catch (error) {
    console.error("Error:", error);
  } finally {
    if (stringToParse != "Crédits insuffisants.") {
      const inputTypes = stringToParse.split(";;;;COPYFROMTO;;;;");
      const manualEntries = inputTypes[0];
      const copiedEntries = inputTypes.slice(1);
      insertManualEntries(manualEntries);
      insertCopiedEntries(copiedEntries);
      return 1;
    } else {
      return -1;
    }
  }
}

/**
 * Insère des données manuelles dans des cellules Excel.
 * @param {string} manualEntries - Les données à insérer, formatées sous forme de chaîne avec des paires "adresse;;valeur" séparées par ";;;;".
 * @returns {Promise<void>} - Aucune valeur de retour.
 * @throws {Error} - Lance une erreur si l'insertion échoue.
 */
async function insertManualEntries(manualEntries: string) {
  const allEntries = manualEntries.split(";;;;");
  const entryDict: { [key: string]: string } = {};
  for (const entryRaw of allEntries) {
    const entrySplit = entryRaw.split(";;");
    entryDict[entrySplit[0]] = entrySplit[1];
  }
  try {
    await Excel.run(async (context) => {
      const sheet = context.workbook.worksheets.getActiveWorksheet();
      for (const [cellAddress, value] of Object.entries(entryDict)) {
        const range = sheet.getRange(cellAddress);
        range.values = [[value]];
      }
      await context.sync();
    });
  } catch (error) {
    console.error("Error inserting manual entries ", error);
  }
}

/**
 * Copie des données d'une plage de cellules vers une autre dans Excel.
 * @param {string[]} copiedEntries - Liste des données à copier, chaque élément étant formaté comme "startingCell;;endingCell;;value".
 * @returns {Promise<void>} - Aucune valeur de retour.
 * @throws {Error} - Lance une erreur si la copie échoue.
 */
async function insertCopiedEntries(copiedEntries: string[]) {
  var entriesList: string[][] = [];
  for (const entryRaw of copiedEntries) {
    const entrySplit = entryRaw.split(";;"); // [startingCell, endingCell, value]
    entriesList.push(entrySplit);
  }
  try {
    await Excel.run(async (context) => {
      const sheet = context.workbook.worksheets.getActiveWorksheet();

      for (const entry of entriesList) {
        const [startingCell, endingCell, value] = entry;

        const sourceRange = sheet.getRange(startingCell);
        const targetRangeAddress = `${startingCell}:${endingCell}`;
        const targetRange = sheet.getRange(targetRangeAddress);
        sourceRange.values = [[value]];
        await context.sync();
        sourceRange.load("address, formulas, values");
        await context.sync();
        targetRange.copyFrom(
          sourceRange,
          Excel.RangeCopyType.all,
          /* skipBlanks */ false,
          /* transpose */ false
        );
      }
      await context.sync();
    });
  } catch (error) {
    console.error("Error inserting copied entries ", error);
  }
}

/**
 * Convertit une plage de cellules Excel en un dictionnaire.
 * @param {boolean} onlySelected - Indique si seules les cellules sélectionnées doivent être utilisées (true pour tout le document, false pour la sélection).
 * @returns {Promise<{ [key: string]: any }>} - Un dictionnaire contenant les adresses des cellules comme clés et leurs valeurs comme valeurs.
 * @throws {Error} - Lance une erreur si la conversion échoue.
 */
async function excelToDict(onlySelected: boolean): Promise<{ [key: string]: any }> {
  try {
    const data: { [key: string]: string } = {};
    await Excel.run(async (context) => {
      const sheet = context.workbook.worksheets.getActiveWorksheet();
      let targetRange: Excel.Range;

      if (!onlySelected) {
        // La boolean qu'on recoit est "Document entier = true"
        targetRange = context.workbook.getSelectedRange();
      } else {
        targetRange = sheet.getUsedRange(true);
      }

      targetRange.load("values");
      await context.sync();

      const rowCount = targetRange.values.length;
      var columnCount = targetRange.values[0].length;

      // Create an array to hold all the cells
      const cells: Excel.Range[][] = [];

      // Load the address for each cell
      for (let rowIndex = 0; rowIndex < rowCount; rowIndex++) {
        const rowCells: Excel.Range[] = [];
        columnCount = targetRange.values[rowIndex].length;
        for (let colIndex = 0; colIndex < columnCount; colIndex++) {
          const cell = targetRange.getCell(rowIndex, colIndex);
          cell.load(["address", "text", "numberFormat"]);
          rowCells.push(cell);
        }
        cells.push(rowCells);
      }

      await context.sync();

      // Iterate over the values and addresses to build the dictionary
      for (let rowIndex = 0; rowIndex < rowCount; rowIndex++) {
        const rowValues = targetRange.values[rowIndex];
        const rowCells = cells[rowIndex];
        for (let colIndex = 0; colIndex < columnCount; colIndex++) {
          const cellValue = rowValues[colIndex];
          if (cellValue == null || cellValue == "") {
            continue;
          }
          const cellAddress = rowCells[colIndex].address;
          const specificAddress: string = cellAddress.split("!").pop() || "";
          const cellNumberFormat =
            Array.isArray(rowCells[colIndex].numberFormat) &&
            Array.isArray(rowCells[colIndex].numberFormat[0])
              ? rowCells[colIndex].numberFormat[0][0]
              : rowCells[colIndex].numberFormat;

          // Check if the cell is formatted as a date
          if (isDateFormat(cellNumberFormat)) {
            const excelSerialDate = typeof cellValue === "number" ? cellValue : null;
            if (excelSerialDate !== null) {
              const jsDate = excelSerialNumberToDate(excelSerialDate);
              data[specificAddress] = jsDate.toISOString().slice(0, 19);
              continue;
            }
          }
          data[specificAddress] = cellValue.toString();
        }
      }
    });
    return data;
  } catch (error) {
    console.error("Error in excelToDict: ", error);
    throw error;
  }
}

/**
 * Vérifie si un format de cellule correspond à un format de date.
 * @param {string} numberFormat - Le format de la cellule.
 * @returns {boolean} - Retourne `true` si le format correspond à un format de date, sinon `false`.
 */
function isDateFormat(numberFormat: string): boolean {
  if (!numberFormat) {
    return false;
  }

  // Common date format patterns in Excel
  const dateFormatPatterns = [
    /^m{1,4}\/d{1,2}\/y{2,4}$/, // e.g., m/d/yy, mm/dd/yyyy
    /^d{1,2}-mmm-y{2,4}$/, // e.g., d-mmm-yy
    /^mmm-y{2,4}$/, // e.g., mmm-yy
    /^mmmm-y{2,4}$/, // e.g., mmmm-yy
    /^mmm d, y{4}$/, // e.g., mmm d, yyyy
    /^d\/m\/yy$/, // e.g., d/m/yy
    /^d\-m\-yy$/, // e.g., d-m-yy
    /^yyyy\-mm\-dd$/, // e.g., 2023-10-16
    /^dd\/mm\/yyyy$/, // e.g., 16/10/2023
    /^dd-mm-yyyy$/, // e.g., 16-10-2023
    /^m\/d\/yyyy$/, // e.g., 10/16/2023
    /^d\.m\.yyyy$/, // e.g., 16.10.2023
    /^mmmm d, yyyy$/, // e.g., October 16, 2023
    /^mmm d, yyyy$/, // e.g., Oct 16, 2023
    /^m\.d\.yyyy$/, // e.g., 10.16.2023
    /^m\/d\/yy h:mm AM\/PM$/, // e.g., 10/16/23 1:45 PM
    /^m\/d\/yy h:mm:ss AM\/PM$/, // e.g., 10/16/23 1:45:30 PM
    /^d-mmm-yy h:mm AM\/PM$/, // e.g., 16-Oct-23 1:45 PM
    /^d-mmm-yy h:mm:ss AM\/PM$/, // e.g., 16-Oct-23 1:45:30 PM
    /^d\/m\/yyyy h:mm$/, // e.g., 16/10/2023 13:45
    /^d\/m\/yyyy h:mm:ss$/, // e.g., 16/10/2023 13:45:30
    // Add more patterns as needed
  ];

  // Check if any pattern matches the numberFormat
  return dateFormatPatterns.some((pattern) => pattern.test(numberFormat));
}

/**
 * Convertit un numéro de série Excel en une date JavaScript.
 * @param {number} serial - Le numéro de série Excel à convertir.
 * @returns {Date} - La date JavaScript correspondante.
 */
function excelSerialNumberToDate(serial: number): Date {
  const excelEpoch = new Date(Date.UTC(1899, 11, 30)); // Excel epoch start (December 30, 1899)
  const jsDate = new Date(excelEpoch.getTime() + serial * 86400000); // 86400000 ms in a day
  return jsDate;
}

/**
 * Envoie un prompt à une API et retourne la réponse.
 * @param {string} prompt - La requête utilisateur à envoyer à l'API.
 * @returns {Promise<string>} - La réponse de l'API sous forme de chaîne.
 * @throws {Error} - Lance une erreur si la requête échoue.
 */
async function promptGemini(prompt: string) {
  try {
    const response = await fetch(`${baseUrl}/callAI`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify([
        "askForFormula", // calledFunction
        {
          // parameters
          data: {
            userRequest: prompt,
          },
        },
      ]),
    });

    const data = await response.json();
    const formula = data.response;
    return formula;
  } catch (error) {
    console.error("Error:", error);
    return "Error accessing backend";
  } finally {
  }
}

/**
 * Interface représentant les données d'un compte utilisateur.
 */
interface AccountData {
  username: string; // Nom d'utilisateur
  email: string; // Adresse e-mail
  password: string; // Mot de passe
}

/**
 * Crée un compte utilisateur via une API.
 * @param {AccountData} data - Données utilisateur (nom, e-mail, mot de passe).
 * @param {string} confirmationCode - Code de confirmation.
 * @returns {Promise<{ message: string }>} - Message de confirmation.
 */
export async function createAccount(
  data: AccountData,
  confirmationCode: string
): Promise<{ message: string }> {
  try {
    const response = await fetch(`${baseUrl}/register`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ ...data, confirmationCode }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Failed to create account");
    }

    const result = await response.json();
    return result;
  } catch (error: any) {
    throw new Error(error.message || "An unexpected error occurred");
  }
}

/**
 * Récupère les données du compte utilisateur.
 * @returns {Promise<{ username: string; email: string; requests_left: number; avatar: number }>} - Données utilisateur.
 */
export async function getAccountData(): Promise<{
  username: string;
  email: string;
  requests_left: number;
  avatar: number;
}> {
  try {
    const response = await fetch(`${baseUrl}/profile`, {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Failed to get account data");
    }
    return await response.json();
  } catch (error: any) {
    throw new Error(error.message || "An unexpected error occurred");
  }
}

/**
 * Met à jour le nom d'utilisateur.
 * @param {string} newUsername - Nouveau nom d'utilisateur.
 * @returns {Promise<{ message: string }>} - Message de confirmation.
 */
export async function updateUsername(newUsername: string): Promise<{ message: string }> {
  try {
    const response = await fetch(`${baseUrl}/profile`, {
      method: "PUT",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ username: newUsername }),
    });

    if (!response.ok) {
      console.error("Error in updateUsername");
      const errorData = await response.json();
      throw new Error(errorData.message || "Failed to update username");
    }

    const result = await response.json();
    return result;
  } catch (error: any) {
    throw new Error(error.message || "An unexpected error occurred");
  }
}

/**
 * Connecte un utilisateur.
 * @param {{ email: string; password: string }} data - Identifiants utilisateur (e-mail et mot de passe).
 * @returns {Promise<{ message: string }>} - Message de confirmation.
 */
export async function seConnecter(data: {
  email: string;
  password: string;
}): Promise<{ message: string }> {
  try {
    const response = await fetch(`${baseUrl}/login`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Failed to login");
    }
    const result = await response.json();
    return result;
  } catch (error: any) {
    // Re-throw the error to be handled by the caller
    throw new Error(error.message || "An unexpected error occurred");
  }
}

/**
 * Déconnecte un utilisateur.
 * @returns {Promise<void>} - Confirmation de déconnexion.
 */
export async function logOut() {
  try {
    const response = await fetch(`${baseUrl}/logout`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Failed to logout");
    }

    const result = await response.json();
    return result;
  } catch (error: any) {
    // Re-throw the error to be handled by the caller
    throw new Error(error.message || "An unexpected error occurred");
  }
}

/**
 * Envoie un e-mail de récupération de mot de passe.
 * @param {string} email - L'adresse e-mail de l'utilisateur.
 * @returns {Promise<any>} - La réponse de l'API en cas de succès.
 * @throws {Error} - Lance une erreur si l'envoi de l'e-mail échoue.
 */
export const sendPasswordRecoveryEmail = async (email: string) => {
  const response = await fetch(`${baseUrl}/sendRecoveryEmail`, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ email }),
  });

  if (!response.ok) {
    const errorResponse = await response.json(); // Get the error response
    throw new Error(errorResponse.message || "Failed to send recovery email"); // Throw specific error message
  }

  return await response.json();
};

/**
 * Vérifie un code de récupération et met à jour le mot de passe.
 * @param {string} email - L'adresse e-mail de l'utilisateur.
 * @param {string} code - Le code de récupération envoyé à l'utilisateur.
 * @param {string} newPassword - Le nouveau mot de passe à définir.
 * @returns {Promise<any>} - La réponse de l'API en cas de succès.
 * @throws {Error} - Lance une erreur si la vérification échoue.
 */
export const verifyRecoveryCode = async (email: string, code: string, newPassword: string) => {
  const response = await fetch(`${baseUrl}/verifyCode`, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ email, code, newPassword }), // Include email, code, and newPassword in the request body
  });

  if (!response.ok) {
    const errorResponse = await response.json(); // Get the error response
    throw new Error(errorResponse.message || "Failed to verify recovery code"); // Throw specific error message
  }

  return await response.json(); // Return the response for successful verification
};

/**
 * Envoie un e-mail de vérification à l'utilisateur.
 * @param {string} email - L'adresse e-mail de l'utilisateur.
 * @param {string} username - Le nom d'utilisateur.
 * @returns {Promise<{ message: string }>} - Un message de confirmation en cas de succès.
 * @throws {Error} - Lance une erreur si l'envoi de l'e-mail échoue.
 */
export const sendVerificationEmail = async (
  email: string,
  username: string
): Promise<{ message: string }> => {
  try {
    const response = await fetch(`${baseUrl}/verifyEmail`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email, username }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Failed to send verification email");
    }

    const result = await response.json();
    return result;
  } catch (error: any) {
    throw new Error(error.message || "An unexpected error occurred");
  }
};

/**
 * Gère le processus de paiement pour un abonnement.
 * @param {string} type - Le type d'abonnement ou de plan choisi.
 * @returns {Promise<void>} - Aucune valeur de retour.
 * @throws {Error} - Lance une erreur si le processus de paiement échoue.
 */
export const handlePayment = async (type: string) => {
  try {
    await getAccountData();
    const response = await fetch(`${baseUrl}/create-subscription`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ plan_name: type }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Failed to create subscription");
    }
    const data = await response.json();
    const sessionUrl = data.session.url;
    const paymentWindow = window.open(sessionUrl, "_blank");
    if (!paymentWindow) {
      throw new Error("Échec de l'ouverture de la fenêtre de paiement");
    }
  } catch (error: any) {
    throw new Error(error.message || "Failed");
  }
};

/**
 * Envoie un message au support technique.
 * @param {{ message: string }} data - Le message à envoyer au support.
 * @returns {Promise<{ message: string }>} - Un message de confirmation en cas de succès.
 * @throws {Error} - Lance une erreur si l'envoi échoue.
 */
export async function sendSupportMessage(data: { message: string }): Promise<{ message: string }> {
  try {
    const response = await fetch(`${baseUrl}/support`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Failed to send message");
    }
    return await response.json();
  } catch (error: any) {
    throw new Error(error.message || "An unexpected error occurred");
  }
}

/**
 * Supprime le compte utilisateur.
 * @returns {Promise<any>} - La réponse de l'API en cas de succès.
 * @throws {Error} - Lance une erreur si la suppression échoue.
 */
export async function deleteAccount() {
  try {
    const response = await fetch(`${baseUrl}/delete-account`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Failed to delete account");
    }

    const result = await response.json();
    return result;
  } catch (error: any) {
    throw new Error(error.message || "An unexpected error occurred");
  }
}
